import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { LinkedDocumentsNavigationResult } from 'features/knowledgeDocumentViewer/hooks/useLinkedDocumentsNavigation'
import {
  AssetSearchItem,
  documentTableRowToSearchItem,
  isDocumentSearchItem,
  RecentItem,
  SearchableItem,
} from 'features/knowledgeDocumentViewer/types/searchableItems'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'

export const MAX_RECENT_ITEMS = 20

const STORAGE_KEY_PREFIX = 'gecko_recent_items'

function getStorageKey(userId: string, siteId: string): string {
  return `${STORAGE_KEY_PREFIX}_${userId}_${siteId}`
}

/**
 * Get all recent items from localStorage.
 * Unrecognised formats (including legacy data) are discarded.
 */
export function getRecentItems(userId: string, siteId: string): RecentItem[] {
  try {
    const key = getStorageKey(userId, siteId)
    const stored = localStorage.getItem(key)
    if (!stored) return []

    const parsed = JSON.parse(stored) as unknown
    if (!Array.isArray(parsed)) return []

    return parsed as RecentItem[]
  } catch (error) {
    console.warn('Failed to read recent items from localStorage:', error)
    return []
  }
}

export function addRecentItem(
  item: SearchableItem,
  userId: string,
  siteId: string
): RecentItem[] {
  try {
    const items = getRecentItems(userId, siteId)

    const filtered = items.filter((r) => r.item.id !== item.id)

    const newRecentItem: RecentItem = {
      item,
      accessedAt: new Date().toISOString(),
    }

    const updatedItems = [newRecentItem, ...filtered].slice(0, MAX_RECENT_ITEMS)

    const key = getStorageKey(userId, siteId)
    localStorage.setItem(key, JSON.stringify(updatedItems))

    return updatedItems
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, clearing old items')
      clearRecentItems(userId, siteId)
    } else {
      console.warn('Failed to save recent item to localStorage:', error)
    }
    return getRecentItems(userId, siteId)
  }
}

/**
 * Remove a single item (by id) from the recents. Used when the underlying
 * entity is deleted so its card cannot dead-end on an unresolvable open.
 */
export function removeRecentItem(
  itemId: string,
  userId: string,
  siteId: string
): RecentItem[] {
  try {
    const items = getRecentItems(userId, siteId)
    const filtered = items.filter((r) => r.item.id !== itemId)
    if (filtered.length === items.length) return items

    const key = getStorageKey(userId, siteId)
    localStorage.setItem(key, JSON.stringify(filtered))
    return filtered
  } catch (error) {
    console.warn('Failed to remove recent item from localStorage:', error)
    return getRecentItems(userId, siteId)
  }
}

export function getRecentsByType<T extends SearchableItem>(
  userId: string,
  siteId: string,
  itemType: T['itemType']
): RecentItem<T>[] {
  const items = getRecentItems(userId, siteId)
  return items.filter((r) => r.item.itemType === itemType) as RecentItem<T>[]
}

export function clearRecentItems(userId: string, siteId: string): void {
  try {
    const key = getStorageKey(userId, siteId)
    localStorage.removeItem(key)
  } catch (error) {
    console.warn('Failed to clear recent items:', error)
  }
}

export function resolveRecentItemsForActiveDocuments({
  recentItems,
  activeDocuments,
}: {
  recentItems: RecentItem[]
  activeDocuments: SystemDocumentEntry[]
}): RecentItem[] {
  const activeDocumentById = new Map(
    activeDocuments.map((entry) => [entry.entry.id, entry])
  )

  return recentItems.flatMap((recent) => {
    if (!isDocumentSearchItem(recent.item)) {
      return [recent]
    }

    const activeDocument = activeDocumentById.get(recent.item.id)
    if (!activeDocument) return []

    return [
      {
        ...recent,
        item: documentTableRowToSearchItem(
          transformSystemDocumentToTableRow(activeDocument)
        ),
      },
    ]
  })
}

export function linkedDocsNavigationToRecentAsset({
  documentIds,
  referenceText,
}: LinkedDocumentsNavigationResult): AssetSearchItem | null {
  if (!referenceText) return null
  return {
    id: `box-${referenceText}`,
    itemType: 'asset',
    displayName: referenceText,
    name: referenceText,
    hasLinkedDatapoints: false,
    linkedDatapointIds: [],
    linkedDocumentIds: documentIds,
    linkedDocumentCount: documentIds.length,
  }
}
